// ** React Imports
import { useEffect, useState } from 'react';

// ** MUI Imports
import Drawer from '@mui/material/Drawer';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Box, { BoxProps } from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

// ** Third Party Imports
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

// ** Icons Imports
import Close from 'mdi-material-ui/Close';

// ** Store Imports
import { client } from 'src/utils/axios-client';
import {
    customersOptionsApiEndpoint,
    playbookOptionsApiEndpoint,
    saveSprintEndpoint,
    sprintStatusOptionsApiEndpoint,
    sprintTypeOptionsApiEndpoint,
} from 'src/configs/endpoints';
import { RecordPermission, SelectOption } from 'src/types/common';
import CustomDatePicker from '../common/CustomDatePicker';
import { Sprint } from '../../stores/SprintStore';
import { ApiResponseType } from '../../types';
import ApplicationStore from '../../stores/ApplicationStore';
import RoleSelector from '../roles/RoleSelector';
import { ClaimType } from '../common/RenderIf';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import FormWithLoader from '../../v2/components/FormWithLoader/FormWithLoader';
import StandardButton from '../../v2/components/Button/StandardButton';
import SaveButton from '../../v2/components/Button/SaveButton';
import SectionAppBar from '../../v2/components/Section/SectionAppBar';
import SimpleDialog from '../common/SimpleDialog';

interface Props {
    open: boolean;
    toggle: () => void;
    sprint: Sprint;
}

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const schema = yup.object().shape({
    name: yup.string().required(),
    playbookId: yup.string().required(),
    startDate: yup.date().required(),
    endDate: yup.date().required(),
    sprintTypeId: yup.string().required(),
    sprintStatusId: yup.string().required(),
    customerId: yup.string().required(),
    locationId: yup.string().required(),
});

const EditSprintDrawer = ({ open, toggle, sprint }: Props) => {
    // ** Props
    const [sprintTypes, setSprintTypes] = useState([] as SelectOption[]);
    const [sprintStatuses, setSprintStatuses] = useState([] as SelectOption[]);
    const [playbooks, setPlaybooks] = useState([] as SelectOption[]);
    const [customers, setCustomers] = useState([] as SelectOption[]);
    const [locations, setLocations] = useState([] as SelectOption[]);

    const {
        reset,
        control,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: sprint,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        reset(sprint);
    }, [sprint]);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const response = await client.get(sprintTypeOptionsApiEndpoint);
                setSprintTypes(response.data);

                const response2 = await client.get(sprintStatusOptionsApiEndpoint);
                setSprintStatuses(response2.data);

                const response3 = await client.get(playbookOptionsApiEndpoint);
                setPlaybooks(response3.data);

                const response4 = await client.get(customersOptionsApiEndpoint);
                setCustomers(response4.data);

                const response5 = await client.get('/api/options/locations');
                setLocations(response5.data);
            } catch (err) {
                console.log(err);
            }
        };

        loadOptions();
    }, []);

    const onSubmit = async (data: Sprint) => {
        try {
            const response = await client.post<ApiResponseType>(saveSprintEndpoint, data);

            if (response.data.succeeded) {
                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Record saved';
                    s.snackbarOpen = true;
                });

                handleClose();
            } else {
                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Error saving record ' + response.data.errors[0].code;
                    s.snackbarOpen = true;
                });
            }
        } catch (err) {
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Error saving record ' + err.errors[0].code;
                s.snackbarOpen = true;
            });
        }
    };

    const handleClose = () => {
        toggle();
    };

    const onRolesChanged = (roleIds: RecordPermission[]) => {};

    return (
        <SimpleDialog open={open} onClose={handleClose} padding={0} fullHeight={false} size="sm">
            <SectionAppBar
                title={`Edit Sprint`}
                subTitle={''}
                buttons={
                    <>
                        <SaveButton
                            size="small"
                            type="submit"
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                            icon={<SaveAltIcon />}
                        >
                            Save
                        </SaveButton>
                        <StandardButton
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={handleClose}
                            icon={<CloseIcon />}
                        >
                            Cancel
                        </StandardButton>
                    </>
                }
            />
            <Box sx={{ p: 5 }}>
                <FormWithLoader onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth sx={{ mb: 6 }}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    value={value}
                                    label="Name"
                                    onChange={onChange}
                                    placeholder="Sprint Name"
                                    error={Boolean(errors.name)}
                                />
                            )}
                        />
                        {/*{errors.name && <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message}</FormHelperText>}*/}
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }}>
                        <Controller
                            name="startDate"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <CustomDatePicker
                                    value={value}
                                    onChange={onChange}
                                    label="Start Date"
                                    error={Boolean(errors.startDate)}
                                />
                            )}
                        />
                        {/*{errors.startDate && <FormHelperText sx={{ color: 'error.main' }}>{errors.startDate.message}</FormHelperText>}*/}
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }}>
                        <Controller
                            name="endDate"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <CustomDatePicker
                                    value={value}
                                    onChange={onChange}
                                    label="End Date"
                                    error={Boolean(errors.endDate)}
                                />
                            )}
                        />
                        {/*{errors.endDate && <FormHelperText sx={{ color: 'error.main' }}>{errors.endDate.message}</FormHelperText>}*/}
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Playbook</InputLabel>
                        <Controller
                            name="playbookId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label="Playbook"
                                    onChange={onChange}
                                    error={Boolean(errors.playbookId)}
                                    inputProps={{ placeholder: 'Playbook' }}
                                >
                                    {playbooks.map((item) => (
                                        <MenuItem key={`sdpb-${item.value}`} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Sprint Type</InputLabel>
                        <Controller
                            name="sprintTypeId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label="Sprint Type"
                                    onChange={onChange}
                                    error={Boolean(errors.sprintTypeId)}
                                    inputProps={{ placeholder: 'Sprint Type' }}
                                >
                                    {sprintTypes.map((item) => (
                                        <MenuItem key={`sdst-${item.value}`} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Sprint Status</InputLabel>
                        <Controller
                            name="sprintStatusId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label="Sprint Status"
                                    onChange={onChange}
                                    error={Boolean(errors.sprintStatusId)}
                                    inputProps={{ placeholder: 'Sprint Status' }}
                                >
                                    {sprintStatuses.map((item) => (
                                        <MenuItem key={`sdss-${item.value}`} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Customer</InputLabel>
                        <Controller
                            name="customerId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label="Customer"
                                    onChange={onChange}
                                    error={Boolean(errors.customerId)}
                                    inputProps={{ placeholder: 'Customer' }}
                                >
                                    {customers.map((item) => (
                                        <MenuItem key={`sdcu-${item.value}`} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 6 }} size="small">
                        <InputLabel>Location</InputLabel>
                        <Controller
                            name="locationId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label="Location"
                                    onChange={onChange}
                                    error={Boolean(errors.locationId)}
                                    inputProps={{ placeholder: 'Location' }}
                                >
                                    {locations.map((item) => (
                                        <MenuItem key={`sdcu-${item.value}`} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                </FormWithLoader>
            </Box>
        </SimpleDialog>
    );
};

export default EditSprintDrawer;
